// src/services/landingPageService.js
import { apiRequest } from './api';

const LANDING_PAGE_API = '/landing_page_api.php';

/**
 * Landing Page Content Management Service
 * Provides methods for Core Admin to manage landing page content
 */

// ============================================
// PUBLIC ENDPOINTS - No authentication required
// ============================================

/**
 * Get all landing page content
 */
export const getAllContent = async () => {
    const response = await apiRequest.get(LANDING_PAGE_API);
    return response.data;
};

/**
 * Get page content by page key (about_us, privacy_policy, terms_of_service)
 */
export const getPageContent = async (pageKey) => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/pages/${pageKey}`);
    return response.data;
};

/**
 * Get all sections
 */
export const getSections = async () => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/sections`);
    return response.data;
};

/**
 * Get carousel items
 */
export const getCarousel = async () => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/carousel`);
    return response.data;
};

/**
 * Get features
 */
export const getFeatures = async () => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/features`);
    return response.data;
};

/**
 * Get testimonials
 */
export const getTestimonials = async () => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/testimonials`);
    return response.data;
};

/**
 * Get FAQ items
 */
export const getFAQ = async () => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/faq`);
    return response.data;
};

// ============================================
// ADMIN ENDPOINTS - Core Admin only
// ============================================

// Page Management
/**
 * Get list of all pages (About, Privacy, Terms)
 */
export const getPagesList = async () => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/admin/pages/list`);
    return response.data;
};

/**
 * Update page metadata (title, subtitle, meta_description)
 */
export const updatePageMeta = async (pageId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/pages/${pageId}`, data);
    return response.data;
};

// Page Section Management
/**
 * Get all sections for a specific page
 */
export const getPageSections = async (pageId) => {
    const response = await apiRequest.get(`${LANDING_PAGE_API}/admin/pages/${pageId}/sections`);
    return response.data;
};

/**
 * Create a new page section
 */
export const createPageSection = async (data) => {
    const response = await apiRequest.post(`${LANDING_PAGE_API}/admin/page-sections`, data);
    return response.data;
};

/**
 * Update page section
 */
export const updatePageSection = async (sectionId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/page-sections/${sectionId}`, data);
    return response.data;
};

/**
 * Delete page section
 */
export const deletePageSection = async (sectionId) => {
    const response = await apiRequest.delete(`${LANDING_PAGE_API}/admin/page-sections/${sectionId}`);
    return response.data;
};

/**
 * Reorder page sections
 */
export const reorderPageSections = async (pageId, orderData) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/pages/${pageId}/reorder`, orderData);
    return response.data;
};

// Section Management (Homepage sections)
/**
 * Update homepage section
 */
export const updateSection = async (sectionId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/sections/${sectionId}`, data);
    return response.data;
};

// Content Management
/**
 * Create new content item
 */
export const createContent = async (data) => {
    const response = await apiRequest.post(`${LANDING_PAGE_API}/admin/content`, data);
    return response.data;
};

/**
 * Update content item
 */
export const updateContent = async (contentId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/content/${contentId}`, data);
    return response.data;
};

/**
 * Delete content item
 */
export const deleteContent = async (contentId) => {
    const response = await apiRequest.delete(`${LANDING_PAGE_API}/admin/content/${contentId}`);
    return response.data;
};

// Carousel Management
/**
 * Create carousel item
 */
export const createCarouselItem = async (data) => {
    const response = await apiRequest.post(`${LANDING_PAGE_API}/admin/carousel`, data);
    return response.data;
};

/**
 * Update carousel item
 */
export const updateCarouselItem = async (carouselId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/carousel/${carouselId}`, data);
    return response.data;
};

/**
 * Delete carousel item
 */
export const deleteCarouselItem = async (carouselId) => {
    const response = await apiRequest.delete(`${LANDING_PAGE_API}/admin/carousel/${carouselId}`);
    return response.data;
};

// Feature Management
/**
 * Create feature
 */
export const createFeature = async (data) => {
    const response = await apiRequest.post(`${LANDING_PAGE_API}/admin/features`, data);
    return response.data;
};

/**
 * Update feature
 */
export const updateFeature = async (featureId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/features/${featureId}`, data);
    return response.data;
};

/**
 * Delete feature
 */
export const deleteFeature = async (featureId) => {
    const response = await apiRequest.delete(`${LANDING_PAGE_API}/admin/features/${featureId}`);
    return response.data;
};

// Testimonial Management
/**
 * Create testimonial
 */
export const createTestimonial = async (data) => {
    const response = await apiRequest.post(`${LANDING_PAGE_API}/admin/testimonials`, data);
    return response.data;
};

/**
 * Update testimonial
 */
export const updateTestimonial = async (testimonialId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/testimonials/${testimonialId}`, data);
    return response.data;
};

/**
 * Delete testimonial
 */
export const deleteTestimonial = async (testimonialId) => {
    const response = await apiRequest.delete(`${LANDING_PAGE_API}/admin/testimonials/${testimonialId}`);
    return response.data;
};

// FAQ Management
/**
 * Create FAQ item
 */
export const createFAQItem = async (data) => {
    const response = await apiRequest.post(`${LANDING_PAGE_API}/admin/faq`, data);
    return response.data;
};

/**
 * Update FAQ item
 */
export const updateFAQItem = async (faqId, data) => {
    const response = await apiRequest.put(`${LANDING_PAGE_API}/admin/faq/${faqId}`, data);
    return response.data;
};

/**
 * Delete FAQ item
 */
export const deleteFAQItem = async (faqId) => {
    const response = await apiRequest.delete(`${LANDING_PAGE_API}/admin/faq/${faqId}`);
    return response.data;
};

const landingPageService = {
    // Public
    getAllContent,
    getPageContent,
    getSections,
    getCarousel,
    getFeatures,
    getTestimonials,
    getFAQ,

    // Admin - Pages
    getPagesList,
    updatePageMeta,
    getPageSections,
    createPageSection,
    updatePageSection,
    deletePageSection,
    reorderPageSections,

    // Admin - Homepage
    updateSection,
    createContent,
    updateContent,
    deleteContent,
    createCarouselItem,
    updateCarouselItem,
    deleteCarouselItem,
    createFeature,
    updateFeature,
    deleteFeature,
    createTestimonial,
    updateTestimonial,
    deleteTestimonial,
    createFAQItem,
    updateFAQItem,
    deleteFAQItem,
};

export default landingPageService;
